import { cva, type VariantProps } from 'class-variance-authority';
import { Match } from 'effect';
import { CalendarCheck, CalendarX, Check } from 'lucide-react';
import type { ComponentProps } from 'react';

import { clsxm } from '@vs-shop/utils';

import { optionalClassName } from '../../internal/optionalClassName.ts';

/**
 * The stock block: one row per place a product can be had from, each reading a
 * mark, where it is held, how much of it, and when it would leave. Flat named
 * parts a tenant arranges in its own JSX, each painting itself.
 *
 *   <ProductStock variant={ProductStockVariant.PANEL}>
 *     <ProductStockList variant={ProductStockVariant.PANEL}>
 *       <ProductStockRow>
 *         <ProductStockIcon variant={ProductStockLevel.AVAILABLE} />
 *         <ProductStockLabel variant={ProductStockLevel.AVAILABLE}>
 *           <ProductStockSource>E-shop</ProductStockSource>
 *           <ProductStockQuantity>{'> 10 ks'}</ProductStockQuantity>
 *         </ProductStockLabel>
 *         <ProductStockNote className="mx-1">–</ProductStockNote>
 *         <ProductStockNote>odešleme</ProductStockNote>
 *         <ProductStockDate>zítra</ProductStockDate>
 *       </ProductStockRow>
 *     </ProductStockList>
 *   </ProductStock>
 *
 * **Nothing here decides what is in stock.** Which rows exist, what each is
 * called, and how much of it there is are answers a tenant computes from its
 * own channel configuration, its cart and its catalogue; the parts take a
 * RESOLVED level and resolved copy and paint a row. That is also why the copy
 * arrives as children throughout — a row's every word is localized, and none of
 * it is this package's.
 *
 * Two independent appearance axes, on different parts:
 *
 * - {@link ProductStockVariant} is the BLOCK's: a panel states availability in
 *   a card of its own, while a product card, a suggestions row and a comparison
 *   cell state the same thing as bare lines inside something else. It rides the
 *   two parts that draw the block.
 * - {@link ProductStockLevel} is a ROW's: the mark and the colour that say
 *   whether the thing is here, coming, or gone. It rides the mark, the label
 *   and the tooltip trigger.
 *
 * They are two unions rather than one because they answer different questions
 * about different parts — a plain list and a panel list both hold available and
 * unavailable rows, so a single axis would have to carry the product of the
 * two.
 *
 * Server-first: every part in this file is a plain server component. The one
 * interactive piece — the tooltip a compact placement puts the list behind —
 * lives on its own client leaf in `productStockTooltip.tsx`.
 */

/**
 * How the block presents itself.
 *
 * `panel` is the free-standing statement: a filled, rounded card with air
 * around the rows, sized against its own width. `plain` is the same rows with
 * the card taken off, for the placements that sit inside something that already
 * draws a box — and it stops being a query container, so the rows step against
 * whatever container encloses the placement rather than against this block.
 */
export const productStockVariants = cva('', {
  variants: {
    variant: {
      panel: '@container',
      plain: '@container-normal',
      unstyled: '',
    },
  },
});

type ProductStockVariantName = NonNullable<VariantProps<typeof productStockVariants>['variant']>;

export type ProductStockVariant = ProductStockVariantName;

/** `satisfies` binds these to the recipes above, so the two cannot drift. */
export const ProductStockVariant = {
  PANEL: 'panel',
  PLAIN: 'plain',
  /** The opt-out: a tenant painting the block itself. */
  UNSTYLED: 'unstyled',
} as const satisfies Record<string, ProductStockVariantName>;

/** Required and closed — not `VariantProps`, which makes it optional. */
export type ProductStockVariantProps = { readonly variant: ProductStockVariant };

/**
 * What a row says about getting the thing: it is here, it is coming, or it is
 * not to be had. The mark, the label colour and the tooltip trigger all read
 * this one axis, so a row cannot show a green tick over muted copy.
 *
 * The colours are one map rather than three copies of the same four arms, and
 * the union is read back off it — so a level cannot exist in the type and be
 * missing from a recipe, and no part can drift from its siblings' palette.
 * Exported for the tooltip's own leaf; never from `index.ts`.
 */
export const productStockLevelClasses = {
  available: 'text-success',
  pending: 'text-warning',
  unavailable: 'text-foreground-muted',
  unstyled: '',
} as const;

type ProductStockLevelName = keyof typeof productStockLevelClasses;

export type ProductStockLevel = ProductStockLevelName;

export const ProductStockLevel = {
  /** Held and ready to leave. */
  AVAILABLE: 'available',
  /** Not held yet, but dated — a pre-order, a supplier's shelf, a restock. */
  PENDING: 'pending',
  /** Gone. */
  UNAVAILABLE: 'unavailable',
  /** The opt-out: a tenant painting the row itself. */
  UNSTYLED: 'unstyled',
} as const satisfies Record<string, ProductStockLevelName>;

export type ProductStockLevelProps = { readonly variant: ProductStockLevel };

export type ProductStockProps = ComponentProps<'div'> & ProductStockVariantProps;

/**
 * The block.
 *
 * Its own element rather than the list's, because the query context and the
 * list are two jobs: an element cannot query itself, so the rows' `@[...]` steps
 * need a box outside the one they sit in. A composition omitting it would still
 * render — with every step silently compiling to nothing — which is why the
 * block owns it instead of tenant JSX.
 */
export const ProductStock = ({ className, variant, ...props }: ProductStockProps) => (
  <div
    data-slot="product-stock"
    className={clsxm(productStockVariants({ variant }), className)}
    {...props}
  />
);

/**
 * The rows.
 *
 * The `plain` arm carries `@sm:px-6` over its own `px-0`, which is inherited
 * behaviour rather than a decision: the padding applies wherever an ANCESTOR
 * container is wide enough, since the block above is not a container in this
 * appearance. Removing it would change what ships.
 */
export const productStockListVariants = cva('', {
  variants: {
    variant: {
      panel: 'bg-background-intense flex flex-col gap-2 rounded-lg px-4 py-6 @sm:px-6',
      plain: 'bg-background flex flex-col gap-2 rounded-lg px-0 py-0 @sm:px-6',
      unstyled: '',
    },
  },
});

export type ProductStockListProps = ComponentProps<'ul'> & ProductStockVariantProps;

/** The list the rows sit in. */
export const ProductStockList = ({ className, variant, ...props }: ProductStockListProps) => (
  <ul
    data-slot="product-stock-list"
    className={clsxm(productStockListVariants({ variant }), className)}
    {...props}
  />
);

/**
 * One place the thing can be had from.
 *
 * Two elements: the list item, and the line inside it that lays the row's
 * pieces out. They wrap rather than shrink — a long warehouse name and a date
 * take a second line instead of squeezing the mark — and the row's own type
 * scale steps with the block's width.
 *
 * The `<li>` carries nothing but its slot: everything a caller passes — the
 * class, the style, the id, the handlers — lands together on the line, so a
 * caller never has to know which of the two boxes its props reached.
 *
 * Carries no appearance axis: a row is laid out the same way whatever it says
 * and wherever the block sits. What changes with the level is the mark and the
 * label, and each of those carries the axis itself.
 */
const productStockRowClasses = 'text-foreground flex flex-wrap items-center text-xs @sm:text-base';

export type ProductStockRowProps = ComponentProps<'div'>;

/** One availability row. */
export const ProductStockRow = ({ className, children, ...props }: ProductStockRowProps) => (
  <li data-slot="product-stock-row">
    <div
      data-slot="product-stock-row-body"
      className={clsxm(productStockRowClasses, className)}
      {...props}
    >
      {children}
    </div>
  </li>
);

/**
 * The row's mark.
 *
 * The glyph is the appearance, not a caller's choice: a tick, a dated calendar
 * and a struck-out calendar are the three things a storefront says about
 * getting a thing, and letting a call site pass its own icon would put the
 * level's meaning back at every call site that renders a row.
 *
 * `@vs-shop/ui`'s `CheckOrCross` was the obvious primitive to reuse and does
 * not fit: it is a two-state boolean mark with no dated arm, and the dated
 * calendar is the whole point of the middle level.
 */
export const productStockIconVariants = cva('mr-2 w-5 shrink-0', {
  variants: { variant: productStockLevelClasses },
});

/**
 * The one axis on which `unstyled` has no meaning: this part IS a glyph, so
 * "no appearance" would still have to draw one. A row that wants no mark
 * composes no mark — an absent part yields no place.
 */
export type ProductStockMarkLevel = Exclude<ProductStockLevel, 'unstyled'>;

export type ProductStockIconProps = Omit<ComponentProps<'svg'>, 'children'> & {
  readonly variant: ProductStockMarkLevel;
};

/** The row's status mark. */
export const ProductStockIcon = ({ className, variant, ...props }: ProductStockIconProps) => {
  const shared = {
    'data-slot': 'product-stock-icon',
    strokeWidth: '2',
    className: clsxm(productStockIconVariants({ variant }), className),
    ...props,
  };

  return Match.value(variant).pipe(
    Match.when('available', () => <Check {...shared} />),
    Match.when('pending', () => <CalendarCheck {...shared} />),
    Match.when('unavailable', () => <CalendarX {...shared} />),
    Match.exhaustive
  );
};

/**
 * What the row is about: where the thing is held, and how much of it.
 *
 * Its colour is the level's, and it is a separate axis from the mark's on
 * purpose — a warehouse that is empty but restocking reads as pending in the
 * copy while the block's mark still answers for the product as a whole.
 */
export const productStockLabelVariants = cva('font-medium', {
  variants: { variant: productStockLevelClasses },
});

export type ProductStockLabelProps = ComponentProps<'div'> & ProductStockLevelProps;

/** The coloured half of the row. */
export const ProductStockLabel = ({ className, variant, ...props }: ProductStockLabelProps) => (
  <div
    data-slot="product-stock-label"
    className={clsxm(productStockLabelVariants({ variant }), className)}
    {...props}
  />
);

/** Where the thing is held — an e-shop, a shop floor, a supplier. */
const productStockSourceClasses = 'mr-1';

export type ProductStockSourceProps = ComponentProps<'span'>;

/** The name of the place. */
export const ProductStockSource = ({ className, ...props }: ProductStockSourceProps) => (
  <span
    data-slot="product-stock-source"
    className={clsxm(productStockSourceClasses, className)}
    {...props}
  />
);

export type ProductStockQuantityProps = ComponentProps<'span'>;

/**
 * How much of it there is.
 *
 * Carries no recipe: the type and the colour come from the label around it, and
 * what a quantity SAYS — a count, a bracket, "the last one", nothing at all
 * when the stock is unbounded — is the tenant's sentence. The element exists so
 * the anatomy is reachable.
 */
export const ProductStockQuantity = ({ className, ...props }: ProductStockQuantityProps) => (
  <span
    data-slot="product-stock-quantity"
    className={optionalClassName(clsxm(className))}
    {...props}
  />
);

/**
 * A word the row adds after the label — what happens next, and the punctuation
 * between. One part rather than two, because the difference between them is
 * which side the space falls on, which is what `className` is for.
 */
const productStockNoteClasses = 'mr-1';

export type ProductStockNoteProps = ComponentProps<'span'>;

/** A trailing word of the row. */
export const ProductStockNote = ({ className, ...props }: ProductStockNoteProps) => (
  <span
    data-slot="product-stock-note"
    className={clsxm(productStockNoteClasses, className)}
    {...props}
  />
);

export type ProductStockDateProps = ComponentProps<'span'>;

/**
 * When it would leave.
 *
 * Carries no recipe for the same reason the quantity does not, and holds a
 * rendered date rather than a value: how a storefront words "tomorrow" is
 * locale work that never enters a shared package.
 */
export const ProductStockDate = ({ className, ...props }: ProductStockDateProps) => (
  <span data-slot="product-stock-date" className={optionalClassName(clsxm(className))} {...props} />
);
